import {
    Button,
    CodeBox,
    ColorPicker,
    ColorUtils,
    IRestaurant,
    SectionLabel,
    StringUtils,
    TextField,
    useConfirmContext,
} from '@localina/core';
import React from 'react';
import { Color } from 'react-color';
import { useTranslation } from 'react-i18next';
import { useUpdateSpecificRestaurantMutation } from '../../../api/queries/restaurants';

interface IProps {
    restaurant: IRestaurant;
    id: string;
}

const JQUERY_URL = 'https://code.jquery.com/jquery-3.7.0.min.js';

// Teal from the redesign palette: white text on it is 7.4:1, and 10.2:1 on the hover shade.
const DEFAULT_BUTTON_COLOR = '#005E6E';
const DEFAULT_BUTTON_HOVER_COLOR = '#004854';

const WidgetIntegration: React.FC<IProps> = (props) => {
    const { t, i18n } = useTranslation();
    const { snackbar } = useConfirmContext();
    const updateRestaurantMutation = useUpdateSpecificRestaurantMutation();

    const [email, setEmail] = React.useState('');
    const [widgetCssUrl, setWidgetCssUrl] = React.useState(props.restaurant.configuration.widgetCssUrl || '');
    const [isEmailValid, setEmailValid] = React.useState(false);
    const [buttonText, setButtonText] = React.useState<string>(
        props.restaurant.configuration.buttonTheme?.text || t('integrations.items.widget.buttons.default-button-text'),
    );
    const [buttonColor, setButtonColor] = React.useState<Color>(
        props.restaurant.configuration.buttonTheme?.buttonColor || DEFAULT_BUTTON_COLOR,
    );
    const [textColor, setTextColor] = React.useState<Color>(
        props.restaurant.configuration.buttonTheme?.textColor || '#FFFFFF',
    );
    const [buttonHoverColor, setButtonHoverColor] = React.useState<Color>(
        props.restaurant.configuration.buttonTheme?.buttonHoverColor || DEFAULT_BUTTON_HOVER_COLOR,
    );
    const [textHoverColor, setTextHoverColor] = React.useState<Color>(
        props.restaurant.configuration.buttonTheme?.textHoverColor || '#FFFFFF',
    );

    const saveWidgetCssUrl = () => {
        updateRestaurantMutation.mutate(
            {
                restaurantId: props.id,
                restaurant: { widgetCssUrl },
            },
            {
                onSuccess: () => {
                    snackbar({
                        msg: t('integrations.items.messages.css-link-successfully-saved'),
                        severity: 'success',
                        autoHideDuration: 2000,
                    });
                },
            },
        );
    };

    React.useEffect(() => {
        setEmailValid(StringUtils.isValidEmail(email));
    }, [email]);

    React.useEffect(() => {
        const createScript = (url: string) => {
            const script = document.createElement('script');
            script.src = url;
            script.type = 'text/javascript';
            return script;
        };

        const jquery = createScript(JQUERY_URL);
        const widget = createScript('./script/widget.js');
        // widget.js calls jQuery the moment it runs, so it is only added once jQuery has loaded.
        jquery.onload = () => document.head.appendChild(widget);
        document.body.appendChild(jquery);

        return () => {
            jquery.onload = null;
            jquery.remove();
            widget.remove();
        };
    }, []);

    const linkOrigin =
        process.env.NODE_ENV === 'development'
            ? window.location.origin.replace(':3000', ':3001') // set port 3001 in development
            : window.location.origin;

    const widgetLink = `${linkOrigin}/widget/?id=${props.id}`;

    const integrationScript =
        `<script src="${JQUERY_URL}" type="text/javascript"></script>\n` +
        '<script src="' +
        linkOrigin +
        '/script/widget.js?reservationButtonColor=' +
        buttonColor.toString() +
        '&reservationButtonHoverColor=' +
        buttonHoverColor.toString() +
        '&reservationButtonTextColor=' +
        textColor.toString() +
        '&reservationButtonTextHoverColor=' +
        textHoverColor.toString() +
        '" type="text/javascript" charset="utf-8"></script>\n' +
        '<script type="text/javascript">jQuery.noConflict();</script>\n' +
        '<a id="localina-button" href="javascript:;" onclick="Localina.startBooking(\'' +
        props.id +
        "', '" +
        linkOrigin +
        '\');">' +
        buttonText +
        '</a>';

    const handleSendEmail = () => {
        window.open(
            `mailto:${email}?subject=${props.restaurant.info.name}&body=${widgetLink}%0D%0A%0D%0A${encodeURIComponent(
                integrationScript,
            )}`,
        );
    };

    const [isHover, setIsHover] = React.useState(false);

    const handleMouseEnter = () => {
        setIsHover(true);
    };

    const handleMouseLeave = () => {
        setIsHover(false);
    };

    const buttonStyle = {
        backgroundColor: isHover ? String(buttonHoverColor) : String(buttonColor),
        color: isHover ? String(textHoverColor) : String(textColor),
    };

    const formatRatio = (ratio: number) =>
        new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(ratio);
    const normalContrast = ColorUtils.contrastRatio(String(textColor), String(buttonColor));
    const hoverContrast = ColorUtils.contrastRatio(String(textHoverColor), String(buttonHoverColor));
    const contrastPasses =
        normalContrast !== undefined &&
        hoverContrast !== undefined &&
        Math.min(normalContrast, hoverContrast) >= ColorUtils.WCAG_AA_NORMAL_TEXT;

    const saveButtonTheme = () => {
        updateRestaurantMutation.mutate(
            {
                restaurantId: props.id,
                restaurant: {
                    buttonTheme: {
                        text: buttonText,
                        buttonColor: buttonColor.toString(),
                        textColor: textColor.toString(),
                        buttonHoverColor: buttonHoverColor.toString(),
                        textHoverColor: textHoverColor.toString(),
                    },
                },
            },
            {
                onSuccess: () => {
                    snackbar({
                        msg: t('integrations.items.messages.successfully-saved'),
                        severity: 'success',
                        autoHideDuration: 2000,
                    });
                },
            },
        );
    };

    const openPopup = () => {
        window.Localina.startBooking(props.id, linkOrigin);
    };

    return (
        <div className="integration widget">
            <SectionLabel value={t('integrations.items.widget.sections.link')} />
            <CodeBox
                value={widgetLink}
                ariaLabel={t('integrations.items.widget.fields.link')}
                copyLabel={t('integrations.items.widget.buttons.copy')}
                copiedLabel={t('integrations.items.widget.feedback')}
            />

            <SectionLabel value={t('integrations.items.widget.sections.style')} />
            <TextField
                label={t('integrations.items.widget.fields.button-text')}
                name={'buttonText'}
                onChange={setButtonText}
                value={buttonText}
                onBlur={saveButtonTheme}
            />
            <div className="widget-colors">
                <ColorPicker
                    title={t('integrations.items.widget.fields.button-color')}
                    onChange={setButtonColor}
                    defaultColor={buttonColor}
                    onClose={saveButtonTheme}
                />
                <ColorPicker
                    title={t('integrations.items.widget.fields.text-color')}
                    onChange={setTextColor}
                    defaultColor={textColor}
                    onClose={saveButtonTheme}
                />
                <ColorPicker
                    title={t('integrations.items.widget.fields.button-hover-color')}
                    onChange={setButtonHoverColor}
                    defaultColor={buttonHoverColor}
                    onClose={saveButtonTheme}
                />
                <ColorPicker
                    title={t('integrations.items.widget.fields.text-hover-color')}
                    onChange={setTextHoverColor}
                    defaultColor={textHoverColor}
                    onClose={saveButtonTheme}
                />
            </div>
            <div className="widget-preview">
                <span className="widget-preview__label">{t('integrations.items.widget.preview')}</span>
                <Button
                    style={buttonStyle}
                    label={buttonText}
                    onClick={openPopup}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                />
            </div>
            {normalContrast !== undefined && hoverContrast !== undefined && (
                <p className={StringUtils.combineStrings(['integration-hint', !contrastPasses && 'is-warning'])}>
                    {t(`integrations.items.widget.contrast.${contrastPasses ? 'pass' : 'fail'}`, {
                        normal: formatRatio(normalContrast),
                        hover: formatRatio(hoverContrast),
                    })}
                </p>
            )}

            <SectionLabel value={t('integrations.items.widget.sections.embed')} />
            <CodeBox
                value={integrationScript}
                ariaLabel={t('integrations.items.widget.fields.integration')}
                copyLabel={t('integrations.items.widget.buttons.copy-integration')}
                copiedLabel={t('integrations.items.widget.feedback')}
            />

            <SectionLabel value={t('integrations.items.widget.sections.css')} />
            <div className="integration-field-action">
                <TextField
                    label={t('integrations.items.widget.fields.cssUrl')}
                    name="widgetCssUrl"
                    value={widgetCssUrl}
                    onChange={setWidgetCssUrl}
                />
                <Button label={t('integrations.items.widget.buttons.save')} secondary onClick={saveWidgetCssUrl} />
            </div>

            <SectionLabel value={t('integrations.items.subtitles.email')} />
            <div className="integration-field-action">
                <TextField
                    label={t('integrations.items.widget.fields.email')}
                    name="email"
                    value={email}
                    onChange={setEmail}
                />
                <Button
                    label={t('integrations.items.widget.buttons.send')}
                    secondary
                    disabled={!isEmailValid}
                    onClick={handleSendEmail}
                />
            </div>
        </div>
    );
};

export default WidgetIntegration;
